import { createVisitorConsentSync } from "@tabbio/utils/analytics/visitor-consent-sync";
import { PRIVACY_NOTICE_VERSION as SHARED_PRIVACY_NOTICE_VERSION } from "@tabbio/utils/analytics/consent";

export const PRIVACY_PREFERENCES_STORAGE_KEY = "tabbio_privacy_preferences";
export const PRIVACY_PREFERENCES_SCHEMA_VERSION = 1 as const;
// Bumped on 2026-09-09 when the Marketing category was added. A choice saved
// under the earlier notice answered an analytics-only question, so it cannot
// stand for the new one; records from before the bump parse as undecided and
// the prompt asks once more, with both purposes described.
export const PRIVACY_NOTICE_VERSION = SHARED_PRIVACY_NOTICE_VERSION;

export type AnalyticsConsent = "unknown" | "granted" | "denied";
/** Same three states, for the marketing purpose. */
export type ConsentState = AnalyticsConsent;
export type ConsentDecisionSource = "prompt" | "settings";

export interface ConsentChoices {
  analytics?: Exclude<ConsentState, "unknown">;
  marketing?: Exclude<ConsentState, "unknown">;
}

export interface PrivacyPreferences {
  schemaVersion: typeof PRIVACY_PREFERENCES_SCHEMA_VERSION;
  noticeVersion: typeof PRIVACY_NOTICE_VERSION;
  /** Product analytics: PostHog, and GA4's analytics_storage signal. */
  analytics: AnalyticsConsent;
  /** Advertising measurement: GA4's ad signals and, on web, the ad tags. */
  marketing: ConsentState;
  decidedAt: string | null;
  updatedAt: string | null;
  source: ConsentDecisionSource | null;
}

export interface PrivacyPreferencesSnapshot {
  isHydrated: boolean;
  preferences: PrivacyPreferences;
}

type Listener = () => void;

const listeners = new Set<Listener>();
let listeningForStorage = false;
let snapshot: PrivacyPreferencesSnapshot = {
  isHydrated: false,
  preferences: createDefaultPrivacyPreferences(),
};
let decisionRevision = 0;
let unpersistedWithdrawal = false;
let hydrationPromise: Promise<PrivacyPreferencesSnapshot> | null = null;

export function createDefaultPrivacyPreferences(): PrivacyPreferences {
  return {
    schemaVersion: PRIVACY_PREFERENCES_SCHEMA_VERSION,
    noticeVersion: PRIVACY_NOTICE_VERSION,
    analytics: "unknown",
    marketing: "unknown",
    decidedAt: null,
    updatedAt: null,
    source: null,
  };
}

export function parsePrivacyPreferences(value: unknown): PrivacyPreferences {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return createDefaultPrivacyPreferences();
  }

  const candidate = value as Partial<PrivacyPreferences>;
  if (
    candidate.schemaVersion !== PRIVACY_PREFERENCES_SCHEMA_VERSION ||
    candidate.noticeVersion !== PRIVACY_NOTICE_VERSION ||
    !isAnalyticsConsent(candidate.analytics)
  ) {
    return createDefaultPrivacyPreferences();
  }

  return {
    schemaVersion: PRIVACY_PREFERENCES_SCHEMA_VERSION,
    noticeVersion: PRIVACY_NOTICE_VERSION,
    analytics: candidate.analytics,
    marketing: isAnalyticsConsent(candidate.marketing)
      ? candidate.marketing
      : "unknown",
    decidedAt:
      typeof candidate.decidedAt === "string" ? candidate.decidedAt : null,
    updatedAt:
      typeof candidate.updatedAt === "string" ? candidate.updatedAt : null,
    source: isConsentDecisionSource(candidate.source) ? candidate.source : null,
  };
}

export function getPrivacyPreferencesSnapshot(): PrivacyPreferencesSnapshot {
  return snapshot;
}

export function subscribePrivacyPreferences(listener: Listener) {
  listeners.add(listener);
  startStorageListener();
  return () => {
    listeners.delete(listener);
  };
}

export function isProductAnalyticsEnabled() {
  return snapshot.isHydrated && snapshot.preferences.analytics === "granted";
}

export function isMarketingEnabled() {
  return snapshot.isHydrated && snapshot.preferences.marketing === "granted";
}

export function hydratePrivacyPreferences() {
  if (snapshot.isHydrated) return Promise.resolve(snapshot);
  if (hydrationPromise) return hydrationPromise;

  const revision = decisionRevision;
  hydrationPromise = readInitialPreferences()
    .then((preferences) => {
      if (revision === decisionRevision)
        setSnapshot({ isHydrated: true, preferences });
      return snapshot;
    })
    .finally(() => {
      hydrationPromise = null;
    });

  return hydrationPromise;
}

export function setAnalyticsConsent(
  analytics: Exclude<AnalyticsConsent, "unknown">,
  source: ConsentDecisionSource,
) {
  return setConsentChoices({ analytics }, source);
}

export function setMarketingConsent(
  marketing: Exclude<ConsentState, "unknown">,
  source: ConsentDecisionSource,
) {
  return setConsentChoices({ marketing }, source);
}

/**
 * Records one or both decisions in a single write, so the prompt's "Allow" and
 * "Decline" answer both purposes atomically rather than racing two saves.
 */
export async function setConsentChoices(
  choices: ConsentChoices,
  source: ConsentDecisionSource,
) {
  ++decisionRevision;
  const now = new Date().toISOString();
  const analytics = choices.analytics ?? snapshot.preferences.analytics;
  const next: PrivacyPreferences = {
    ...snapshot.preferences,
    schemaVersion: PRIVACY_PREFERENCES_SCHEMA_VERSION,
    noticeVersion: PRIVACY_NOTICE_VERSION,
    analytics,
    marketing: choices.marketing ?? snapshot.preferences.marketing,
    decidedAt: snapshot.preferences.decidedAt ?? now,
    updatedAt: now,
    source,
  };
  const withdraws =
    choices.analytics === "denied" || choices.marketing === "denied";

  // A failed save must reach the caller instead of displaying "saved".
  // Withdrawal still disables capture immediately for this session.
  if (withdraws) {
    unpersistedWithdrawal = true;
    setSnapshot({ isHydrated: true, preferences: next });
  }
  window.localStorage.setItem(
    PRIVACY_PREFERENCES_STORAGE_KEY,
    JSON.stringify(next),
  );
  if (
    window.localStorage.getItem(PRIVACY_PREFERENCES_STORAGE_KEY) !==
    JSON.stringify(next)
  )
    throw new Error("Privacy preferences could not be saved");
  unpersistedWithdrawal = false;
  setSnapshot({ isHydrated: true, preferences: next });
  if (analytics === "denied") {
    try {
      window.localStorage.removeItem("tabbio_posthog_anonymous_id");
    } catch {
      // The saved denial and in-memory guard already prevent capture.
    }
  }

  return next;
}

// Unknown local state stays undecided. An older cross-origin receipt must never
// resurrect consent after this app's storage was cleared or a withdrawal failed.
async function readInitialPreferences(): Promise<PrivacyPreferences> {
  return readPersistedPreferences();
}
const syncVisitorReceipt = createVisitorConsentSync(async () => {
  const { getAPIBaseURL } = await import("./api-url");
  return getAPIBaseURL();
});

function readPersistedPreferences(): PrivacyPreferences {
  if (typeof window === "undefined") return createDefaultPrivacyPreferences();
  try {
    const value = window.localStorage.getItem(PRIVACY_PREFERENCES_STORAGE_KEY);
    const preferences = value
      ? parsePrivacyPreferences(JSON.parse(value))
      : createDefaultPrivacyPreferences();
    if (preferences.analytics !== "granted") {
      window.localStorage.removeItem("tabbio_posthog_anonymous_id");
    }
    return preferences;
  } catch {
    return createDefaultPrivacyPreferences();
  }
}

function setSnapshot(next: PrivacyPreferencesSnapshot) {
  const hadDecision =
    snapshot.preferences.analytics !== "unknown" ||
    snapshot.preferences.marketing !== "unknown";
  const hasDecision =
    next.preferences.analytics !== "unknown" ||
    next.preferences.marketing !== "unknown";
  snapshot = next;
  if (
    typeof window !== "undefined" &&
    window.location &&
    next.isHydrated &&
    (hadDecision || hasDecision)
  )
    void syncVisitorReceipt({
      analytics: next.preferences.analytics === "granted",
      marketing: next.preferences.marketing === "granted",
    });
  listeners.forEach((listener) => listener());
}

function startStorageListener() {
  if (listeningForStorage || typeof window === "undefined") return;
  listeningForStorage = true;
  window.addEventListener("pageshow", (event) => {
    if (event.persisted && !unpersistedWithdrawal) {
      ++decisionRevision;
      setSnapshot({
        isHydrated: true,
        preferences: readPersistedPreferences(),
      });
    }
  });
  window.addEventListener("storage", (event) => {
    if (event.key !== PRIVACY_PREFERENCES_STORAGE_KEY && event.key !== null)
      return;
    ++decisionRevision;
    unpersistedWithdrawal = false;
    let preferences = createDefaultPrivacyPreferences();
    try {
      preferences = event.newValue
        ? parsePrivacyPreferences(JSON.parse(event.newValue))
        : createDefaultPrivacyPreferences();
    } catch {
      // Invalid cross-tab values safely return to the undecided state.
    }
    if (preferences.analytics !== "granted") {
      try {
        window.localStorage.removeItem("tabbio_posthog_anonymous_id");
      } catch {
        // Storage can be blocked; the in-memory guard still prevents capture.
      }
    }
    setSnapshot({ isHydrated: true, preferences });
  });
}

function isAnalyticsConsent(value: unknown): value is AnalyticsConsent {
  return value === "unknown" || value === "granted" || value === "denied";
}

function isConsentDecisionSource(
  value: unknown,
): value is ConsentDecisionSource {
  return value === "prompt" || value === "settings";
}

export function resetPrivacyPreferencesForTests() {
  snapshot = {
    isHydrated: false,
    preferences: createDefaultPrivacyPreferences(),
  };
  hydrationPromise = null;
  decisionRevision = 0;
  unpersistedWithdrawal = false;
  listeners.clear();
}
